// Fetch wrapper for the changelist server: same-origin credentials, the CSRF
// header on writes, and re-authentication when the server says so. Failed
// requests go to the app log (feedback/logs.ts). A client
// the server does not serve (the browser extension) names the server with
// `useServer` instead.
import { httpError, isServerRateLimit, retryAfterSeconds, type Transport } from './cache';
import { MOCK } from '../mock/flag';
import { identityGeneration, identityUser, requireGeneration } from './identity';
import { githubWriteWords, serverErrorWords } from './serverErrors';
import { newRequestId, noteResponse, NoResponseError, REQUEST_ID_HEADER } from './request';
import { appLog } from '../feedback/logs';

let mockTransport: Transport | null = null;
let devTransport: ((url: string, init: RequestInit, write: boolean) => Promise<Response>) | null = null;

/** The server's origin for a client it does not serve; null in its own pages. */
let server: string | null = null;

/**
 * Send every request to the changelist server at `origin` (`scheme://host[:port]`,
 * nothing else), with its cookies, for a client the server does not serve: the
 * browser extension, whose host permission for that origin lets its cookies
 * through. Such a client has no page to send to sign-in, so a 401 is left to
 * the caller as an `HttpError`, with no navigation and no event.
 */
export function useServer(origin: string) {
  const u = new URL(origin);
  if (u.origin !== origin) throw new Error(`not an origin: ${origin}`);
  server = origin;
}

/** A transport told whether the request is a write (see `transport`). */
type Sender = (url: string, init: RequestInit, write: boolean) => Promise<Response>;

async function transportFor(): Promise<Sender> {
  // Compile-time gates, so production builds without VITE_MOCK drop the mock.
  if ((import.meta.env.DEV || import.meta.env.VITE_MOCK === '1') && MOCK) {
    if (!mockTransport) mockTransport = (await import('../mock/server')).mockFetch;
    return mockTransport;
  }
  // The Vite dev server only (`import.meta.hot` is undefined in a build):
  // reads wait while the API server behind it is being built; writes never do.
  if (import.meta.hot && !server) return (devTransport ??= (await import('../../dev/backend')).devTransport);
  return (url, init) => fetch(url, init);
}

/**
 * Paths whose 401 means "signed out" to the caller rather than "sign in
 * again": the session probe, and logout (the session was already gone).
 */
const SIGNED_OUT_PROBES = ['/api/me', '/auth/logout'];

function pathOf(url: string): string {
  try {
    return new URL(url, 'http://x').pathname;
  } catch {
    return url;
  }
}

/**
 * Transport that names every request with a fresh `X-Request-Id` (recorded
 * with its response, see `requestOf`, or carried by the `NoResponseError`
 * it fails with when nothing answered) and applies the 401 policy to every
 * response: any 401 from the
 * server (an expired, revoked or logged-out session, or `reauth_required`)
 * sends the user to sign in, except from the paths in `SIGNED_OUT_PROBES`,
 * whose callers handle it, and during sign-out (`beginSignOut`).
 *
 * `write`: whether the request changes something (`isWrite` knows a
 * GraphQL mutation from a query). It matters only under the Vite dev
 * server, where a read waits for a backend that is being built and a
 * write never does.
 */
export const transport = async (url: string, init: RequestInit, write?: boolean): Promise<Response> => {
  const generation = identityGeneration();
  const t = await transportFor();
  requireGeneration(generation);
  const method = (init.method ?? 'GET').toUpperCase();
  // A caller that does not say counts anything but a GET or HEAD as a write.
  const changes = write ?? (method !== 'GET' && method !== 'HEAD');
  const headers = new Headers(init.headers);
  const expectedUser = identityUser();
  if (expectedUser === null && ![...SIGNED_OUT_PROBES, '/api/config'].includes(pathOf(url)))
    throw new Error('The session ended; reload this page.');
  if (expectedUser && !SIGNED_OUT_PROBES.includes(pathOf(url))) headers.set('X-Changelist-User', expectedUser);
  if (method !== 'GET' && method !== 'HEAD') headers.set('X-Changelist', '1');
  const sent = { method, path: pathOf(url), requestId: newRequestId(), at: Date.now() };
  headers.set(REQUEST_ID_HEADER, sent.requestId);
  const remote = server;
  let res: Response;
  try {
    res = await t(
      remote ? new URL(url, remote).href : url,
      { ...init, headers, credentials: remote ? 'include' : 'same-origin' },
      changes,
    );
  } catch (e) {
    // An abort is the caller's own doing, not a failure to reach the server.
    if (e instanceof DOMException && e.name === 'AbortError') throw e;
    appLog.request({ ...sent, status: null });
    throw new NoResponseError({ ...sent, status: null }, e);
  }
  noteResponse(res, sent);
  // Failed requests, with their IDs, for a feedback report's app logs.
  if (res.status >= 400) appLog.request({ ...sent, requestId: res.headers.get(REQUEST_ID_HEADER) || sent.requestId, status: res.status });
  requireGeneration(generation);
  if (res.status === 401 && !remote && !SIGNED_OUT_PROBES.includes(pathOf(url))) {
    signedOut = true;
    // `account_changed`: this page is another account's (the cookie changed
    // under it). The session clears the page; signing in would race that.
    if ((await errorCode(res)) === 'account_changed') {
      if (typeof window !== 'undefined') window.dispatchEvent(new Event('changelist:session-invalidated'));
    } else reauthenticate();
  }
  return res;
};

let signedOut = false;

/** The `error` code of a JSON error response, read from a copy. */
async function errorCode(res: Response): Promise<string | null> {
  try {
    const body: unknown = await res.clone().json();
    return typeof body === 'object' && body !== null && typeof (body as { error?: unknown }).error === 'string'
      ? (body as { error: string }).error
      : null;
  } catch {
    return null;
  }
}

/** True once any request found the session gone (writers stop retrying). */
export function sessionLost(): boolean {
  return signedOut;
}

export function loginUrl(returnTo = location.pathname + location.search): string {
  return '/auth/login?return_to=' + encodeURIComponent(returnTo);
}

let redirecting = false;
let signingOut = false;

/**
 * Sign-out is ending the session: until `cancelSignOut`, a 401 from any
 * request is expected and does not send the user to sign in again.
 */
export function beginSignOut() {
  signingOut = true;
}

export function cancelSignOut() {
  signingOut = false;
}

export function reauthenticate() {
  if (redirecting || signingOut || typeof location === 'undefined') return;
  redirecting = true;
  location.assign(loginUrl());
}

export interface ApiResponse<T> {
  body: T;
  headers: Headers;
}

/**
 * Waits before each retry of a request the server answered 503
 * `installations_pending` (it has not yet learned the App's installations,
 * which takes seconds after it starts). After the last one the request
 * fails as before.
 */
const ACCESS_RETRY_MS = [1000, 2000, 3000, 5000, 5000, 5000, 5000, 5000];

let checking = 0;
const checkingListeners = new Set<(checking: boolean) => void>();

function setChecking(delta: number) {
  checking += delta;
  for (const fn of checkingListeners) fn(checking > 0);
}

/**
 * Calls `fn` now and whenever requests start or stop waiting for the
 * server to learn its installations ("checking access"). Returns the
 * unsubscribe function.
 */
export function onCheckingAccess(fn: (checking: boolean) => void): () => void {
  checkingListeners.add(fn);
  fn(checking > 0);
  return () => checkingListeners.delete(fn);
}

/** Whether a request changes something: not a GET, and not a GraphQL query (which is a POST too). */
function isWrite(opts: { method?: string; body?: unknown }): boolean {
  const method = (opts.method ?? 'GET').toUpperCase();
  if (method === 'GET' || method === 'HEAD') return false;
  const query = (opts.body as { query?: unknown } | undefined)?.query;
  return typeof query !== 'string' || /^\s*mutation\b/.test(query.replace(/^(\s|#[^\n]*)*/, ''));
}

/** `api`, also returning the response headers (rate-limit headers on a 200). */
export async function apiResponse<T>(
  path: string,
  opts: { method?: string; body?: unknown; headers?: Record<string, string>; signal?: AbortSignal } = {},
): Promise<ApiResponse<T>> {
  const generation = identityGeneration();
  const headers: Record<string, string> = { Accept: 'application/json', ...(opts.headers ?? {}) };
  let body: BodyInit | undefined;
  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }
  let res: Response;
  let parsed: unknown;
  let waiting = false;
  try {
    for (let attempt = 0; ; attempt++) {
      res = await transport(path, { method: opts.method ?? 'GET', headers, body, signal: opts.signal }, isWrite(opts));
      const text = await res.text();
      requireGeneration(generation);
      parsed = null;
      if (text) {
        try {
          parsed = JSON.parse(text);
        } catch {
          parsed = text;
        }
      }
      const pending =
        res.status === 503 && (parsed as { error?: unknown } | null)?.error === 'installations_pending';
      if (!pending || attempt >= ACCESS_RETRY_MS.length) break;
      if (!waiting) setChecking(+1);
      waiting = true;
      await new Promise((r) => setTimeout(r, ACCESS_RETRY_MS[attempt]));
    }
  } finally {
    if (waiting) setChecking(-1);
  }
  if (!res.ok) {
    if (isServerRateLimit(res.status, parsed)) {
      const wait = retryAfterSeconds(res);
      throw httpError(res, parsed, `Changeling is limiting requests; try again ${wait ? `in ${wait} s` : 'shortly'}`);
    }
    const words = serverErrorWords(parsed) ?? (isWrite(opts) ? githubWriteWords(parsed) : null);
    if (words) throw httpError(res, parsed, words.advice ? `${words.text} ${words.advice}` : words.text);
    const detail = (parsed as { detail?: string; error?: string; message?: string } | null) ?? null;
    throw httpError(res, parsed, detail?.detail ?? detail?.error ?? detail?.message ?? `HTTP ${res.status}`);
  }
  return { body: parsed as T, headers: res.headers };
}

export async function api<T>(
  path: string,
  opts: { method?: string; body?: unknown; headers?: Record<string, string>; signal?: AbortSignal } = {},
): Promise<T> {
  return (await apiResponse<T>(path, opts)).body;
}
